/**
 * Autoplay unlock.
 *
 * Browsers refuse to start audio until the document has seen a user gesture.
 * For a messenger this is not a nicety: the incoming-call ringtone is the
 * only reason a backgrounded tab gets noticed, and a blocked AudioContext
 * fails silently -- `resume()` neither throws nor resumes, it just stays
 * suspended. The result is a call that rings visually and makes no sound,
 * with nothing in the console.
 *
 * Ported from midcore-messenger-ui/src/hooks/useAutoplayUnlock.ts.
 */

import type { AutoplayApi } from './types.js';

/**
 * One AudioContext per document. Creating one per check leaks contexts --
 * browsers cap them (Chrome at 6) and then throw, which would break the
 * ringtone in a long session.
 */
let probe: AudioContext | null = null;

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function ensureProbe(): AudioContext | null {
  if (probe) return probe;
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  try {
    probe = new Ctor();
    return probe;
  } catch {
    return null;
  }
}

/**
 * Read the live state without letting the type checker narrow it away.
 *
 * Inside `unlock` the compiler sees an early `state === 'running'` return and
 * concludes the value can never be 'running' afterwards -- true of the
 * variable, false of the object, because `resume()` mutates it. Reading
 * through a function call keeps the check honest.
 */
function isRunning(ctx: AudioContext): boolean {
  return ctx.state === 'running';
}

export const autoplay: AutoplayApi = {
  isBlocked(): boolean {
    const ctx = ensureProbe();
    // No AudioContext at all is not "blocked" -- it is "impossible", and the
    // caller should hide the affordance rather than offer to unlock.
    if (!ctx) return false;
    // 'interrupted' is iOS taking the audio session away for a phone call or
    // a Siri invocation. It needs the same unlock path as 'suspended', and
    // treating it as fine is how a ringtone goes silent after an interruption.
    return !isRunning(ctx);
  },

  async unlock(): Promise<boolean> {
    const ctx = ensureProbe();
    if (!ctx) return false;
    if (isRunning(ctx)) return true;

    try {
      await ctx.resume();
    } catch {
      return false;
    }

    // resume() resolves even when the browser declined, so the state after
    // the await is the only trustworthy answer. This is the trap the Meet
    // hook documents: awaiting resume() and assuming success gives you a
    // silent ringtone and a green log line.
    return isRunning(ctx);
  },
};

/** Test seam. Never called in production. */
export function _resetAutoplayProbe(): void {
  probe = null;
}
